<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const sections = [
  {
    title: '🏷️ Isi Barcode di Halaman Produk',
    icon: 'ri-price-tag-3-line',
    color: 'primary',
    content: `
      <p class="mb-4">
        <strong>Barcode</strong> beda dari <strong>SKU</strong> — SKU adalah kode internal
        toko Anda sendiri (boleh bebas format), sedangkan barcode biasanya sudah tercetak
        di kemasan produk dari pabrik (kode angka panjang, mis. EAN-13). Barcode dipakai
        khusus untuk <strong>scan cepat di kasir</strong>.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Buka menu <strong>Produk</strong>, buat produk baru atau edit yang
          sudah ada</li>
        <li class="mb-2">Pada setiap <strong>varian</strong> produk, isi kolom
          <strong>"Barcode (opsional)"</strong> — arahkan kursor ke kolom itu lalu scan
          pakai alat scanner USB, atau ketik manual</li>
        <li class="mb-0">Klik <strong>Simpan</strong></li>
      </ol>
      <VAlert type="info" variant="tonal">
        💡 Kolom Barcode <strong>opsional</strong> — kalau kosong, produk tetap bisa dijual
        normal lewat grid produk di kasir, cuma tidak bisa dipanggil lewat scan.
      </VAlert>
    `,
  },
  {
    title: '🔫 Cara Kerja di Kasir (POS)',
    icon: 'ri-scan-2-line',
    color: 'success',
    content: `
      <p class="mb-4">
        Di halaman <strong>POS Kasir</strong>, kolom pencarian di atas grid produk
        (<em>"Cari produk... (F1)"</em>) dipakai untuk 2 hal sekaligus: cari nama produk
        biasa, <strong>dan</strong> scan barcode.
      </p>
      <ol class="mb-4">
        <li class="mb-2">Klik kolom pencarian (atau tekan <strong>F1</strong>) supaya
          kursor aktif di situ</li>
        <li class="mb-2">Scan barcode pakai alat scanner (otomatis mengetik angka lalu
          menekan Enter), <strong>atau</strong> ketik manual angka barcode-nya lalu tekan
          <strong>Enter</strong></li>
        <li class="mb-0">Kalau barcode cocok, produk/varian itu <strong>otomatis masuk
          keranjang</strong> dan muncul notifikasi kecil ("... ditambahkan lewat scan
          barcode")</li>
      </ol>
      <VAlert type="success" variant="tonal">
        ✅ Kalau yang Anda ketik <strong>bukan</strong> barcode yang cocok (mis. Anda
        memang cuma mengetik nama produk lalu tekan Enter), sistem otomatis melanjutkan
        sebagai pencarian nama biasa — tidak ada error yang mengganggu alur kasir.
      </VAlert>
    `,
  },
  {
    title: '🧭 Kapan Fitur Ini Muncul?',
    icon: 'ri-eye-line',
    color: 'info',
    content: `
      <p class="mb-0">
        Scan barcode di kolom pencarian POS hanya aktif kalau toggle
        <strong>"Aktifkan Fitur Retail"</strong> sudah dinyalakan di Pengaturan → Tipe
        Bisnis. Lihat panduan <strong>"Toggle F&amp;B &amp; Retail"</strong> kalau belum
        pernah mengaturnya.
      </p>
    `,
  },
]
</script>

<template>
  <div>
    <RouterLink to="/userguide/retail">
      <VBtn
        variant="text"
        prepend-icon="ri-arrow-left-line"
        class="mb-2"
      >
        Kembali ke Fitur Retail
      </VBtn>
    </RouterLink>

    <div class="d-flex align-center mb-4">
      <VAvatar
        color="success"
        variant="tonal"
        size="64"
        rounded="lg"
        class="me-4"
      >
        <VIcon
          icon="ri-barcode-line"
          size="36"
        />
      </VAvatar>
      <div>
        <h4 class="text-h4 font-weight-bold mb-1">
          Scan Barcode di Kasir
        </h4>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Isi barcode produk sekali, lalu scan/ketik + Enter untuk masuk keranjang otomatis
        </p>
      </div>
    </div>

    <VExpansionPanels
      variant="accordion"
      multiple
    >
      <VExpansionPanel
        v-for="(section, index) in sections"
        :key="index"
      >
        <VExpansionPanelTitle>
          <VIcon
            :icon="section.icon"
            :color="section.color"
            class="me-3"
          />
          {{ section.title }}
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div v-html="section.content" />
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- FAQ -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      ❓ Pertanyaan yang Sering Ditanyakan
    </h5>
    <VExpansionPanels variant="accordion">
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Apa bedanya kolom Barcode dengan SKU?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <strong>SKU</strong> adalah kode internal toko Anda sendiri (bebas format, mis.
          "KOPI-001"). <strong>Barcode</strong> biasanya kode angka standar dari kemasan
          pabrik, dan khusus dipakai untuk scan cepat di kasir — keduanya boleh diisi
          berbeda, dan barcode boleh dikosongkan kalau tidak dibutuhkan.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Bisakah 1 produk punya beberapa barcode?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Saat ini 1 varian hanya bisa punya 1 barcode. Kalau produk punya beberapa
          varian (mis. ukuran kecil/besar), tiap varian bisa diisi barcode masing-masing
          secara terpisah.
        </VExpansionPanelText>
      </VExpansionPanel>
      <VExpansionPanel>
        <VExpansionPanelTitle class="text-body-2 font-weight-medium">
          Saya scan tapi produk tidak masuk keranjang, kenapa?
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          Kemungkinan besar barcode tersebut belum diisi di halaman Produk (varian mana
          pun), atau kolom pencarian di POS sedang tidak fokus/aktif saat scanner
          mengirim datanya. Pastikan kursor ada di kolom pencarian sebelum scan.
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <!-- Related -->
    <h5 class="text-h5 font-weight-bold mt-6 mb-3">
      🔗 Halaman Terkait
    </h5>
    <VRow>
      <VCol
        v-for="link in [
          { title: 'Toggle F&B & Retail', sub: 'Aktifkan dulu fitur Retail di Pengaturan', icon: 'ri-store-3-line', color: 'primary', to: { name: 'userguide-retail-toggle' } },
          { title: 'POS Kasir', sub: 'Panduan lengkap kasir dan transaksi', icon: 'ri-shopping-cart-2-line', color: 'primary', to: { name: 'userguide-operasional-pos-kasir' } },
          { title: 'Produk', sub: 'Kelola katalog produk dan variant', icon: 'ri-shopping-bag-3-line', color: 'info', to: { name: 'userguide-produk-inventory-produk' } },
        ]"
        :key="link.title"
        cols="12"
        md="6"
      >
        <RouterLink
          :to="link.to"
          class="text-decoration-none"
        >
          <VCard
            variant="outlined"
            hover
          >
            <VCardText class="d-flex align-center gap-3 py-3">
              <VAvatar
                :color="link.color"
                variant="tonal"
                size="40"
                rounded
              >
                <VIcon
                  :icon="link.icon"
                  size="22"
                />
              </VAvatar>
              <div>
                <p class="text-body-2 font-weight-bold mb-0">
                  {{ link.title }}
                </p>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ link.sub }}
                </p>
              </div>
            </VCardText>
          </VCard>
        </RouterLink>
      </VCol>
    </VRow>
  </div>
</template>
